import React, { createContext, useState } from 'react';
import { WML, MatchType, VarianceType } from './types/AlertResults';
import produce from 'immer';

type AlertResultsProviderProps = {
  children: React.ReactElement | React.ReactElement[];
};

export type HeaderFilters = {
  sourceSiteId: string | null;
  sourceCategoryId: string | null;
  insertionDate: string | null;
  uniqueIdentifier: string | null;
  wml: keyof typeof WML | null;
  variance: [number, number] | null;
  alertId: string | null;
  sourceRatingCount: string | null;
};
export type SidebarFilters = {
  priceType: VarianceType | null;
  matchType: MatchType | null;
  clientRegion: string | null;
  competitorSiteId: string | null;
  competitorSellerName: string | null;
  competitorFulfillerName: string | null;
};
export type Filters = HeaderFilters & SidebarFilters;

type AlertResultsProviderState = {
  headerFilters: HeaderFilters;
  sidebarFilters: SidebarFilters;
  forcedReload: object | null;
};

type AlertResultsContextValue = AlertResultsProviderState & {
  reloadAlertResultsList: () => void;
  changeHeaderFilters: <T extends keyof HeaderFilters>(value: HeaderFilters[T], filterKey: T) => void;
  changeSidebarFilters: <T extends keyof SidebarFilters>(value: SidebarFilters[T], filterKey: T) => void;
  clearSidebarFilters: () => void;
};

const initialState: AlertResultsProviderState = {
  headerFilters: {
    sourceSiteId: null,
    sourceCategoryId: null,
    insertionDate: null,
    uniqueIdentifier: null,
    wml: null,
    variance: null,
    alertId: null,
    sourceRatingCount: null,
  },
  sidebarFilters: {
    priceType: VarianceType.Lowest,
    matchType: MatchType.ALL,
    clientRegion: null,
    competitorSiteId: null,
    competitorSellerName: null,
    competitorFulfillerName: null,
  },
  forcedReload: null,
};

export const AlertResultsContext = createContext<AlertResultsContextValue>({
  ...initialState,
  reloadAlertResultsList: () => {},
  changeHeaderFilters: () => {},
  changeSidebarFilters: () => {},
  clearSidebarFilters: () => {},
});

export const AlertResultsProvider: React.FC<AlertResultsProviderProps> = ({ children }) => {
  const [state, setState] = useState(initialState);

  const value: AlertResultsContextValue = {
    headerFilters: state.headerFilters,
    sidebarFilters: state.sidebarFilters,
    forcedReload: state.forcedReload,
    reloadAlertResultsList: () => {
      setState((prevState) => ({ ...prevState, forcedReload: {} }));
    },
    changeHeaderFilters: <T extends keyof HeaderFilters>(value: HeaderFilters[T], filterKey: T) => {
      setState(
        produce((draft: AlertResultsProviderState) => {
          draft.headerFilters[filterKey] = value;
        })
      );
    },
    changeSidebarFilters: <T extends keyof SidebarFilters>(value: SidebarFilters[T], filterKey: T) => {
      setState(
        produce((draft: AlertResultsProviderState) => {
          draft.sidebarFilters[filterKey] = value;
        })
      );
    },
    clearSidebarFilters: () => {
      setState((prev) => ({ ...prev, sidebarFilters: { ...initialState.sidebarFilters } }));
    },
  };

  return <AlertResultsContext.Provider value={value}>{children}</AlertResultsContext.Provider>;
};
